/**
 * Graphes — la figure : sommets nommés, arêtes (ou arcs orientés, courbés
 * quand l'arc retour existe), boucles, poids, chemin mis en valeur.
 */

import { cn } from '@jeraisonne/ui';
import { FIGURE_BG_CLASS, HaloText, INK, arrowHeadPoints } from '../../ui/svg-parts';
import { NAMES } from './markov';

export const W = 320;
export const H = 260;
const R = 20;

export interface GEdge {
  from: number;
  to: number;
  label?: string;
  strong?: boolean;
}

const LAYOUTS: Record<number, Array<{ x: number; y: number }>> = {
  2: [
    { x: 95, y: 130 },
    { x: 225, y: 130 },
  ],
  3: [
    { x: 160, y: 74 },
    { x: 248, y: 202 },
    { x: 72, y: 202 },
  ],
  4: [
    { x: 80, y: 66 },
    { x: 240, y: 66 },
    { x: 240, y: 196 },
    { x: 80, y: 196 },
  ],
};

const EDGE = 'var(--color-ink-500)';
const STRONG = 'var(--color-primary-500)';

export function GraphFigure({
  n,
  edges,
  oriented,
  label,
}: {
  n: number;
  edges: readonly GEdge[];
  oriented: boolean;
  label: string;
}) {
  const pos = LAYOUTS[n]!;
  const cx = pos.reduce((s, p) => s + p.x, 0) / n;
  const cy = pos.reduce((s, p) => s + p.y, 0) / n;
  const has = (a: number, b: number) => edges.some((e) => e.from === a && e.to === b);
  // un seul trait par paire en non orienté
  const shown = oriented ? edges : edges.filter((e) => e.from <= e.to);
  return (
    <div className="mx-auto w-full max-w-[26rem]">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className={cn('border-ink-100 block w-full rounded-2xl border', FIGURE_BG_CLASS)}
        role="img"
        aria-label={label}
      >
        {shown.map((e, k) => {
          const color = e.strong ? STRONG : EDGE;
          const width = e.strong ? 4 : 2;
          const p = pos[e.from]!;
          if (e.from === e.to) {
            // boucle, tournée vers l'extérieur
            let vx = p.x - cx;
            let vy = p.y - cy;
            const l = Math.hypot(vx, vy) || 1;
            vx /= l;
            vy /= l;
            const c = { x: p.x + vx * (R + 8), y: p.y + vy * (R + 8) };
            return (
              <g key={k} data-testid={`edge-${e.from}-${e.to}`}>
                <circle cx={c.x} cy={c.y} r={15} fill="none" stroke={color} strokeWidth={width} />
                {e.label && (
                  <HaloText
                    x={p.x + vx * (R + 38)}
                    y={p.y + vy * (R + 38)}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize={13}
                    fontWeight={700}
                    fill={INK}
                  >
                    {e.label}
                  </HaloText>
                )}
              </g>
            );
          }
          const q = pos[e.to]!;
          const dx = q.x - p.x;
          const dy = q.y - p.y;
          const d = Math.hypot(dx, dy);
          const nx = -dy / d;
          const ny = dx / d;
          const bend = oriented && has(e.to, e.from) ? 26 : 0;
          const ctl = { x: (p.x + q.x) / 2 + nx * bend, y: (p.y + q.y) / 2 + ny * bend };
          const dir = (a: { x: number; y: number }, b: { x: number; y: number }) => {
            const l = Math.hypot(b.x - a.x, b.y - a.y) || 1;
            return { x: (b.x - a.x) / l, y: (b.y - a.y) / l };
          };
          const u0 = dir(p, ctl);
          const u1 = dir(ctl, q);
          const s = { x: p.x + u0.x * R, y: p.y + u0.y * R };
          const t = { x: q.x - u1.x * (R + 1), y: q.y - u1.y * (R + 1) };
          const mid = {
            x: 0.25 * p.x + 0.5 * ctl.x + 0.25 * q.x,
            y: 0.25 * p.y + 0.5 * ctl.y + 0.25 * q.y,
          };
          const lab = { x: mid.x + nx * (bend ? 12 : 11), y: mid.y + ny * (bend ? 12 : 11) };
          return (
            <g key={k} data-testid={`edge-${e.from}-${e.to}`}>
              <path
                d={`M ${s.x.toFixed(1)} ${s.y.toFixed(1)} Q ${ctl.x.toFixed(1)} ${ctl.y.toFixed(1)} ${t.x.toFixed(1)} ${t.y.toFixed(1)}`}
                fill="none"
                stroke={color}
                strokeWidth={width}
              />
              {oriented && (
                <polygon
                  points={arrowHeadPoints(t.x - u1.x * 10, t.y - u1.y * 10, t.x, t.y, 11)}
                  fill={color}
                />
              )}
              {e.label && (
                <HaloText
                  x={lab.x}
                  y={lab.y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={13}
                  fontWeight={700}
                  fill={INK}
                >
                  {e.label}
                </HaloText>
              )}
            </g>
          );
        })}
        {pos.map((p, i) => (
          <g key={i}>
            <circle
              cx={p.x}
              cy={p.y}
              r={R}
              fill="var(--fig-bg, #fff)"
              stroke={INK}
              strokeWidth={2}
            />
            <text
              x={p.x}
              y={p.y}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={16}
              fontWeight={700}
              fill={INK}
            >
              {NAMES[i]}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}
